// App.jsx
import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { Spinner } from "@fluentui/react-components";

import AppShell from "./components/Layout/AppShell";
import LoginPage from "./components/Auth/LoginPage";
import Dashboard from "./components/Dashboard/Dashboard";
import AnalyticsPage from "./components/Charts/AnalyticsPage";
import DataEntryForm from "./components/DataEntry/DataEntryForm";
import ExcelUpload from "./components/DataEntry/ExcelUpload";
import ReportsPage from "./components/Reports/ReportsPage";
import EntityManagerPage from "./components/EntityManager/EntityManagerPage";
import UserAccessPage from "./components/Admin/UserAccessPage";
import DeptOwnerMapPage from "./components/Admin/DeptOwnerMapPage";
import ActivityTrackerPage from "./components/ActivityTracker/ActivityTrackerPage";
import ActivityBulkUpload from "./components/ActivityTracker/ActivityBulkUpload";
import TrainingRosterPage from "./components/Training/TrainingRosterPage";
import CFOIssuesPage from "./components/CFOIssues/CFOIssuesPage";
import BenchmarkPage from "./components/Benchmark/BenchmarkPage";
import BenchmarkAnalyticsPage from "./components/Benchmark/BenchmarkAnalyticsPage";
import MarketIntelligencePage from "./components/Benchmark/MarketIntelligencePage";
import BenchmarkBulkUpload from "./components/Benchmark/BenchmarkBulkUpload";
import { useSCMData } from "./hooks/useSCMData";
import { useEntitySchema } from "./hooks/useEntitySchema";
import { useDateFilter } from "./hooks/useDateFilter";
import { SchemaContext } from "./context/SchemaContext";
import { UserAccessProvider } from "./context/UserAccessContext";

function AuthenticatedApp() {
  const { records, transactions, loading, isDemoMode, refresh, bulkAdd, getDetail, editTransaction, deleteTransaction } = useSCMData();
  const schemaCtx = useEntitySchema();
  const dateFilter = useDateFilter(records);

  return (
    <UserAccessProvider>
      <SchemaContext.Provider value={schemaCtx}>
        <Routes>
          <Route path="/" element={<AppShell isDemoMode={isDemoMode} />}>
            <Route index element={<Dashboard records={records} loading={loading} refresh={refresh} getDetail={getDetail} dateFilter={dateFilter} dieselTransactions={transactions.dieselRecords || []} logisticsTransactions={transactions.logisticsRecords || []} />} />
            <Route path="charts" element={<AnalyticsPage dateFilter={dateFilter} dieselTransactions={transactions.dieselRecords || []} />} />
            <Route path="data-entry" element={<DataEntryForm onBulkAdd={bulkAdd} loading={loading} />} />
            <Route path="excel-upload" element={<ExcelUpload onBulkUpload={bulkAdd} />} />
            <Route path="reports" element={<ReportsPage transactions={transactions} onEdit={editTransaction} onDelete={deleteTransaction} onAdd={(col, data) => bulkAdd({ [col]: [data] })} />} />
            <Route path="entities" element={<EntityManagerPage />} />
            <Route path="activity-tracker" element={<ActivityTrackerPage />} />
            <Route path="activity-tracker/bulk-upload" element={<ActivityBulkUpload />} />
            <Route path="training-roster" element={<TrainingRosterPage />} />
            <Route path="cfo-issues" element={<CFOIssuesPage />} />
            <Route path="benchmark" element={<BenchmarkPage />} />
            <Route path="benchmark/analytics" element={<BenchmarkAnalyticsPage />} />
            <Route path="benchmark/intelligence" element={<MarketIntelligencePage />} />
            <Route path="benchmark/bulk-upload" element={<BenchmarkBulkUpload />} />
            <Route path="admin/users" element={<UserAccessPage />} />
            <Route path="admin/dept-owner-map" element={<DeptOwnerMapPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </SchemaContext.Provider>
    </UserAccessProvider>
  );
}

export default function App() {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();

  if (inProgress === InteractionStatus.Startup || inProgress === InteractionStatus.HandleRedirect) {
    return (
      <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"#001A3D" }}>
        <Spinner size="huge" appearance="inverted" label="Initializing..." />
      </div>
    );
  }

  if (!isAuthenticated) return <LoginPage />;
  return <AuthenticatedApp />;
}
